import "./Layout.css";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import CustomHeader from "components/header/CustomHeader";
import { useAppSelector } from "redux/hooks";
import ReduxUserApi from "redux/user/ReduxUserApi";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ReduxCommonApi from "redux/common/ReduxCommonApi";
import AllRoutes from "constants/AllRoutes";
import { RoleNames } from "models/auth/RoleNames";
import PathItem from "models/route/PathItem";
import { hasRole } from "util/UtilityFunctions";
import EmailIcon from "@mui/icons-material/Email";
import { getCurrentPathItem } from "pages/main/Main";
import { createApi } from "util/ApiCreator";
import { useTestMode } from "context/TestModeContext";
import { adminTheme } from "theme/adminTheme";

export default function AdminLayout() {
  const api = useRef(createApi("account"));
  useTestMode();

  const common = useAppSelector((state) => state.common);
  const me = useAppSelector((s) => s?.me?.info);
  const navigate = useNavigate();
  const location = useLocation();
  const [shrink, setShrink] = useState<boolean>(false);

  const [items, setItems] = useState<PathItem[]>([]);

  useEffect(() => {
    let naujiroutes = AllRoutes[1].paths
      .filter((item: PathItem) => item?.menuIcon && item?.menuLabel)
      .filter(
        (item: PathItem) =>
          (item.forAdmin && hasRole(RoleNames.ADMIN, me?.roles)) ||
          (item.forUser && hasRole(RoleNames.USER, me?.roles)),
      );
    setItems(naujiroutes);
  }, [me]);

  useEffect(() => {
    ReduxUserApi.getAndSetMe(api);
  }, []);

  const navigateToTarget = (item: PathItem) => {
    navigate(item.path);
    ReduxCommonApi.closeSideMenu();
  };

  useEffect(() => {
    const maybePath = getCurrentPathItem(location.pathname);
    setShrink(maybePath?.path === "/process/execution/:id/:stepIndex");
  }, [location.pathname, me]);

  if (common?.sideMenuIsHidden) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: adminTheme.colors.backgroundAlt,
      }}
    >
      {me && <CustomHeader />}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {items && items.length > 0 && (
          <Drawer
            sx={{
              width: "260px",
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: "260px",
                zIndex: "0",
                height: common?.sideMenuIsPermanent ? "100%" : undefined,
                boxSizing: common?.sideMenuIsPermanent ? "border-box" : undefined,
                position: "relative",
                bgcolor: adminTheme.colors.background,
                borderRight: `1px solid ${adminTheme.colors.border}`,
              },
            }}
            variant={common?.sideMenuIsPermanent ? "permanent" : undefined}
            open={common?.sideMenuIsOpen}
            onClose={() => ReduxCommonApi.closeSideMenu()}
            anchor={"left"}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
              role={"presentation"}
            >
              {/* Navigation Items */}
              <List sx={{ flex: 1, px: 1.5, py: 2 }}>
                {items.map((item) => {
                  // Use startsWith to match nested routes (e.g., /process/execution/27 matches /process/execution)
                  const isActive = location.pathname === item.path ||
                    (item.path !== "/" && location.pathname.startsWith(item.path + "/"));
                  return (
                    <ListItem
                      key={item.menuLabel}
                      disablePadding
                      sx={{ mb: 0.5 }}
                    >
                      <ListItemButton
                        onClick={() => navigateToTarget(item)}
                        sx={{
                          borderRadius: adminTheme.borderRadius.sm,
                          py: 1.5,
                          px: 2,
                          transition: adminTheme.transitions.fast,
                          bgcolor: isActive
                            ? "rgba(99, 102, 241, 0.1)"
                            : "transparent",
                          color: isActive
                            ? adminTheme.colors.primary
                            : adminTheme.colors.textSecondary,
                          "&:hover": {
                            bgcolor: isActive
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(99, 102, 241, 0.05)",
                            color: adminTheme.colors.primary,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          {item.menuIcon ? item.menuIcon : <EmailIcon />}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isActive ? 600 : 500,
                            }}
                          >
                            {item.menuLabel}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: adminTheme.colors.backgroundAlt,
          }}
        >
          <Outlet />
        </Box>

        {shrink && common?.sideMenuIsPermanent && (
          <Box sx={{ width: "340px", flexShrink: 0 }} />
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          color: adminTheme.colors.textMuted,
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `1px solid ${adminTheme.colors.border}`,
          px: 3,
          py: 2,
          bgcolor: adminTheme.colors.background,
          flexWrap: "wrap",
          gap: 2,
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <Typography variant="body2">
          © recodinAI {new Date().getFullYear()}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "11px" }}>
          Projektą finansuoja Europos Sąjungos fondai
        </Typography>
      </Box>
    </Box>
  );
}
